import { useEffect, useRef } from "react";
import notifySound from "../../assets/chat_notify.mp3";

const getNotificationVolume = () => {
  const storedVolume = Number(localStorage.getItem("volume"));

  if (!Number.isFinite(storedVolume)) {
    return 1;
  }

  return Math.max(0, Math.min(1, storedVolume));
};

const InternalChatNotificationListener = ({ socket, user }) => {
  const audioRef = useRef(null);

  useEffect(() => {
    if (typeof Audio === "undefined") {
      return undefined;
    }

    const audio = new Audio(notifySound);
    audio.preload = "auto";
    audioRef.current = audio;

    const unlockAudio = () => {
      audio.muted = true;
      audio.volume = 0;
      audio.currentTime = 0;

      const restoreAudio = () => {
        audio.muted = false;
        audio.volume = getNotificationVolume();
      };

      const playback = audio.play();
      if (playback && typeof playback.then === "function") {
        playback.then(() => {
          audio.pause();
          audio.currentTime = 0;
          restoreAudio();
        }).catch(restoreAudio);
      } else {
        audio.pause();
        audio.currentTime = 0;
        restoreAudio();
      }
    };

    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("touchstart", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (
      !socket ||
      typeof socket.on !== "function" ||
      typeof socket.off !== "function" ||
      !user?.id ||
      !user?.companyId
    ) {
      return undefined;
    }

    const onInternalChatMessage = (data) => {
      if (data?.action !== "new-message") {
        return;
      }

      const isSender = Number(data?.newMessage?.senderId) === Number(user.id);
      const isChatParticipant = data?.chat?.users?.some(
        ({ userId }) => Number(userId) === Number(user.id)
      );

      if (isSender || !isChatParticipant || !audioRef.current) {
        return;
      }

      const audio = audioRef.current;
      audio.volume = getNotificationVolume();
      audio.currentTime = 0;
      audio.play().catch(() => {});
    };

    const eventName = `company-${user.companyId}-chat`;
    socket.on(eventName, onInternalChatMessage);

    return () => socket.off(eventName, onInternalChatMessage);
  }, [socket, user?.id, user?.companyId]);

  return null;
};

export default InternalChatNotificationListener;
